"use client";

import { AnimatePresence, motion } from "framer-motion";
import type { ReactNode } from "react";
import { EASE } from "@/lib/motion";
import type { Bonus } from "@/lib/api/incentives";
import { countdownTag } from "@/lib/smartico/countdown";
import { toPlainText } from "@/lib/format/html";
import { cn } from "@/lib/cn";
import { Icon } from "@/components/ui/Icon";
import { useModalChrome } from "@/components/ui/Modal";
import { RemoteImage } from "@/components/ui/RemoteImage";
import { Chip, ProgressBar } from "@/components/ui/primitives";

/* The reward detail, on the kit's `desktop-reward-modal` frame: banner art with
   the close control sitting on it, then a centred column — title, description,
   a chip row (type, countdown, game), the value in the success colour, the
   wagering strip, a centred Terms & Conditions disclosure, and the action.

   One modal serves both things the rewards page opens: a bonus programme
   (BonusModal below adapts one) and a player's own claimable reward, which is
   what the kit actually drew — its frame has a value and a Claim button, and
   only a reward has either.

   All platform HTML is flattened through toPlainText, as everywhere else. */

export type RewardModalChip = { label: string; urgent?: boolean };

export type RewardModalView = {
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  iconUrl?: string | null;
  chips: RewardModalChip[];
  /* The reward's worth, already formatted — "$50.00", "100 spins". */
  value?: string | null;
  /* A measured percentage when one exists (bonus rollups state a target). */
  progress?: { percent: number; label: string } | null;
  /* A reward's tranche states only what remains, so it gets a figure, not a
     fill — an invented percentage is worse than none. */
  wageringNote?: string | null;
  terms?: string | null;
  signedOutHint?: string | null;
};

export function RewardModal({
  view,
  action,
  onClose,
}: {
  view: RewardModalView | null;
  action?: ReactNode;
  onClose: () => void;
}) {
  const panel = useModalChrome(Boolean(view), onClose);

  const terms = view?.terms ? toPlainText(view.terms) : null;

  return (
    <AnimatePresence>
      {view && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-[2px]"
          />

          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label={view.title}
            initial={{ opacity: 0, scale: 0.95, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 10 }}
            transition={{ duration: 0.32, ease: EASE }}
            className="modal-shadow scrollbar-none relative flex max-h-[90vh] w-full max-w-[440px] flex-col overflow-y-auto rounded-2xl border border-border-input bg-elevation-1"
          >
            {/* The close control sits on the banner, kit-style — a dark square
                that reads on any art. Rendered before the banner so it also
                anchors correctly when there is no image. */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close reward details"
              className="absolute right-[16px] top-[16px] z-10 grid h-[32px] w-[32px] place-items-center rounded-lg bg-scrim text-fg transition-colors hover:bg-scrim-strong"
            >
              <Icon name="close" size={16} />
            </button>

            {view.imageUrl ? (
              <div className="relative aspect-[440/240] w-full shrink-0 overflow-hidden bg-elevation-2">
                <RemoteImage src={view.imageUrl} sizes="440px" />
                <span className="absolute inset-0 bg-gradient-to-t from-scrim to-transparent" />
              </div>
            ) : (
              <div className="grid h-[120px] w-full shrink-0 place-items-center bg-elevation-2 text-fg-disabled">
                {view.iconUrl ? (
                  <span className="relative h-[70px] w-[70px] overflow-hidden rounded-md">
                    <RemoteImage src={view.iconUrl} width={70} height={70} />
                  </span>
                ) : (
                  <Icon name="image" size={35} />
                )}
              </div>
            )}

            <div className="flex flex-col items-center gap-[12px] p-[24px] text-center">
              <h2 className="text-[17.5px] font-bold leading-[1.4] text-fg">{view.title}</h2>

              {view.description && (
                <p className="whitespace-pre-line text-[12px] leading-[1.6] text-fg-subtle">
                  {toPlainText(view.description)}
                </p>
              )}

              {view.chips.length > 0 && (
                <div className="flex flex-wrap items-center justify-center gap-[6px]">
                  {view.chips.map((chip) => (
                    <Chip
                      key={chip.label}
                      className={cn(
                        chip.urgent ? "bg-error text-white" : "bg-elevation-3",
                        "capitalize tabular-nums",
                      )}
                    >
                      {chip.label}
                    </Chip>
                  ))}
                </div>
              )}

              {view.value && (
                <p className="text-[15.75px] font-black leading-[1.6] tabular-nums text-success-fg">
                  {view.value}
                </p>
              )}

              {view.progress && (
                <div className="w-full">
                  <ProgressBar
                    value={Math.round(view.progress.percent)}
                    height={26}
                    label={view.progress.label}
                  />
                </div>
              )}

              {view.wageringNote && <WageringStrip note={view.wageringNote} />}

              {view.signedOutHint && (
                <p className="w-full rounded-lg bg-elevation-2 px-[12px] py-[8px] text-[12px] leading-[1.6] text-fg-subtle">
                  {view.signedOutHint}
                </p>
              )}

              {terms && (
                <details className="group/terms w-full">
                  <summary className="flex cursor-pointer list-none items-center justify-center gap-[6px] py-[4px] text-[12px] font-bold leading-[1.6] text-fg [&::-webkit-details-marker]:hidden">
                    Terms &amp; Conditions
                    <span className="text-fg-subtle transition-transform duration-200 group-open/terms:rotate-180">
                      <Icon name="chevron-down" size={14} />
                    </span>
                  </summary>
                  <p className="whitespace-pre-line pt-[8px] text-left text-[12px] leading-[1.6] text-fg-subtle">
                    {terms}
                  </p>
                </details>
              )}

              {action && <div className="w-full pt-[4px]">{action}</div>}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/* The kit's wagering strip — label left, figure right, the brand gradient as
   the fill, advancing over a darker track exactly as far as the measurement
   says.

   Without a percent the track stays EMPTY and the figure does the talking.
   This used to fill the whole strip on the reasoning that "a fill that guesses
   is worse than none" — but a full bar is not a neutral absence of
   information, it is a claim, and the claim it makes is "requirement met". A
   reward showing `5.75 USDSM left to wager` under a solid bar said the
   opposite of the truth. Empty under-claims instead, which is the safe
   direction, and `trancheProgressView` now supplies a real percentage for
   almost every wagering reward anyway.

   Gradients are inline through the theme tokens rather than generated
   utilities, after one arbitrary selector silently missed the stylesheet. */
const STRIP_FILL = "linear-gradient(90deg, var(--color-brand), var(--color-brand-bold))";

export function WageringStrip({ note, percent }: { note: string; percent?: number }) {
  const clamped = percent === undefined ? null : Math.min(100, Math.max(0, percent));

  return (
    <div
      className="relative flex h-[26px] w-full items-center justify-between gap-[8px] overflow-hidden rounded-rounded bg-elevation-3 px-[12px] text-[11px] font-bold leading-none"
      role={clamped === null ? undefined : "progressbar"}
      aria-valuenow={clamped ?? undefined}
      aria-valuemin={clamped === null ? undefined : 0}
      aria-valuemax={clamped === null ? undefined : 100}
    >
      {clamped !== null && (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 rounded-rounded"
          style={{ width: `${clamped}%`, background: STRIP_FILL }}
        />
      )}
      <span className="relative text-fg">Wagering Requirement</span>
      <span className="relative tabular-nums text-fg">{note}</span>
    </div>
  );
}

/* A programme from /incentives/bonuses, adapted to the modal's view. Kept as
   the rewards page's existing call shape so the page did not have to move. */
export function BonusModal({
  bonus,
  chips,
  runs,
  progress,
  signedIn,
  onClose,
}: {
  bonus: Bonus | null;
  chips: string[];
  runs: string | null;
  progress: { percent: number; label: string } | null;
  signedIn: boolean;
  onClose: () => void;
}) {
  const countdown = bonus?.endAt ? countdownTag(Date.parse(bonus.endAt), Date.now()) : null;

  const view: RewardModalView | null = bonus
    ? {
        title: bonus.name,
        description: bonus.description,
        imageUrl: bonus.imageUrl,
        iconUrl: bonus.iconUrl,
        chips: [
          ...chips.map((label) => ({ label })),
          ...(countdown ? [{ label: countdown.label, urgent: countdown.isUrgent }] : []),
          ...(runs && !countdown ? [{ label: runs }] : []),
        ],
        progress,
        terms: bonus.termsAndConditions,
        signedOutHint: signedIn ? null : "Sign in to see your progress toward this reward.",
      }
    : null;

  return <RewardModal view={view} onClose={onClose} />;
}
